import type { ClientFormSchema } from '@src/clients/schemas/ClientFormSchema';
import { Checkbox } from '@src/common/components/Checkbox';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { ControllerFormNumberInput } from '@src/orders/PermitFormSections/ControllerFormNumberInput';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { FormDatePicker } from '@src/orders/PermitFormSections/FormDatePicker';
import {
  InsuranceAddressNumberField,
  InsuranceAgentField,
  InsuranceCompanyField,
  InsuranceCoverageAmountField,
  InsuranceEffectiveDateField,
  InsuranceExperienceDateField,
  InsurancePhoneNumberField,
  InsurancePolicyNumberField,
} from '@src/permitField/InsuranceInformationFields';
import type { Control, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

import { ClientFormSection } from './ClientFormSection';

interface Props<T extends FieldValues> {
  control: Control<T>;
}

export const InsuranceForm = ({
  control,
}: Props<yup.InferType<typeof ClientFormSchema>>) => {
  const { t } = useTranslation();

  return (
    <Stack direction="column" gap={theme.spacing['9']}>
      <ClientFormSection title={t('editClient.information')}>
        <Stack gap={theme.spacing['8']}>
          <ControllerFormTextInput
            control={control}
            fieldName="insuranceInformation.insuranceCompany"
            label={t(`fields.${InsuranceCompanyField.fieldName}`)}
          />
          <ControllerFormTextInput
            control={control}
            fieldName="insuranceInformation.insuranceAddressNumber"
            label={t(`fields.${InsuranceAddressNumberField.fieldName}`)}
          />
          <Stack direction="row" gap={theme.spacing['6']}>
            <ControllerFormTextInput
              control={control}
              label={t(`fields.${InsuranceAgentField.fieldName}`)}
              fieldName="insuranceInformation.insuranceAgentAmount"
            />
            <ControllerFormTextInput
              control={control}
              label={t(`fields.${InsurancePhoneNumberField.fieldName}`)}
              fieldName="insuranceInformation.insurancePhoneNumber"
            />
          </Stack>
        </Stack>
      </ClientFormSection>
      <ClientFormSection title={t('editClient.policy')}>
        <Stack gap={theme.spacing['8']}>
          <Stack direction="row" gap={theme.spacing['6']}>
            <ControllerFormTextInput
              control={control}
              label={t(`fields.${InsurancePolicyNumberField.fieldName}`)}
              fieldName="insuranceInformation.insurancePolicyNumber"
            />
            <ControllerFormNumberInput
              control={control}
              label={t(`fields.${InsuranceCoverageAmountField.fieldName}`)}
              fieldName="insuranceInformation.insuranceCoverageAmount"
            />
          </Stack>

          <Stack direction="row" gap={theme.spacing['6']}>
            <FormDatePicker
              control={control}
              isUTC
              fieldName="insuranceInformation.insuranceExperienceDate"
              label={t(`fields.${InsuranceExperienceDateField.fieldName}`)}
            />
            <FormDatePicker
              control={control}
              isUTC
              fieldName="insuranceInformation.insuranceEffectiveDate"
              label={t(`fields.${InsuranceEffectiveDateField.fieldName}`)}
            />
          </Stack>
          <Controller
            name="insuranceInformation.insuranceIsOnFileWithState"
            control={control}
            defaultValue={false}
            render={({ field: { value, onChange, ...field } }) => (
              <Checkbox
                {...field}
                checked={value || false}
                onChange={(e) => {
                  onChange(e.target.checked);
                }}
                label={t('fields.insuranceIsOnFileWithState')}
              />
            )}
          />
        </Stack>
      </ClientFormSection>
    </Stack>
  );
};
